import { useCallback, useEffect, useMemo, useState } from 'react'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import { addFieldPassChatMember, buildFieldPassUrl, createFieldPass, listFieldPassesFromFirestore, revokeFieldPass } from '../../services/fieldPassService'
import { getChecklistItemsView } from '../../services/checklistReader'
import type { FieldPassRecord } from '../../types/fieldPass'
import type { ChecklistWorkflowStatus } from '../../types/schema'

export type ChecklistOption = {
  id: string
  title: string
  workflowStatus: ChecklistWorkflowStatus
}

export type MemberOption = {
  uid: string
  name: string
  role: string
}

export type FieldPassView = 'ACTIVE' | 'ARCHIVED'

export const isFieldPassArchived = (pass: FieldPassRecord): boolean =>
  pass.status === 'REVOKED' || pass.status === 'EXPIRED' || pass.status === 'COMPLETED'

/**
 * Shared state for the Field Passes tab. InvitesHubV2 calls this hook and
 * passes the returned model down to the listing (middle stage) and the
 * sidecar (right panel) so both surfaces see the same passes, options,
 * selection, and busy tokens without a Context boundary.
 *
 * The hook stays cheap when `canManage` is false — the fetch effects
 * short-circuit and nothing is queried.
 */
export const useFieldPassesModel = (workspaceId: string, canManage: boolean) => {
  const [passes, setPasses] = useState<FieldPassRecord[]>([])
  const [options, setOptions] = useState<ChecklistOption[]>([])
  const [members, setMembers] = useState<MemberOption[]>([])
  const [memberSelection, setMemberSelection] = useState<Record<string, string>>({})
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [deleteOnCompletion, setDeleteOnCompletion] = useState(false)
  const [lastLink, setLastLink] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [recordsUnavailable, setRecordsUnavailable] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [view, setView] = useState<FieldPassView>('ACTIVE')
  const [selectedPassId, setSelectedPassId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(true)

  const activePasses = useMemo(() => passes.filter((pass) => !isFieldPassArchived(pass)), [passes])
  const archivedPasses = useMemo(() => passes.filter(isFieldPassArchived), [passes])
  const visiblePasses = view === 'ACTIVE' ? activePasses : archivedPasses
  const selectedPass = selectedPassId ? passes.find((pass) => pass.id === selectedPassId) ?? null : null

  const refreshPasses = useCallback(async () => {
    if (!canManage) return
    try {
      const next = await listFieldPassesFromFirestore(workspaceId)
      setPasses(next)
      setRecordsUnavailable(false)
    } catch (caught) {
      console.warn('[useFieldPassesModel] read failed', caught)
      setRecordsUnavailable(true)
    }
  }, [canManage, workspaceId])

  useEffect(() => {
    if (!workspaceId || !canManage) return
    void refreshPasses()
  }, [canManage, refreshPasses, workspaceId])

  useEffect(() => {
    if (!workspaceId || !canManage) return
    void getChecklistItemsView({ workspaceId })
      .then((items) => {
        const next = items
          .filter((item) => item.isHelperVisible !== false)
          .map((item): ChecklistOption => ({
            id: item.id,
            title: item.title?.trim() || 'Untitled task',
            workflowStatus: (item.workflowStatus || (item.isCompleted ? 'COMPLETED' : 'ASSIGNED')) as ChecklistWorkflowStatus,
          }))
          .sort((a, b) => a.title.localeCompare(b.title))
        setOptions(next)
      })
      .catch(() => setOptions([]))
  }, [canManage, workspaceId])

  useEffect(() => {
    if (!workspaceId || !canManage) return
    void getDocs(query(collection(firestoreDb, 'workspace_members'), where('workspaceId', '==', workspaceId)))
      .then((snapshot) => {
        const next = snapshot.docs
          .map((docSnapshot): MemberOption | null => {
            const data = docSnapshot.data() as Record<string, unknown>
            if (data.status !== 'ACTIVE' || data.role === 'HELPER') return null
            const uid = typeof data.uid === 'string' && data.uid ? data.uid : typeof data.userId === 'string' && data.userId ? data.userId : ''
            if (!uid) return null
            return {
              uid,
              name: typeof data.displayName === 'string' && data.displayName ? data.displayName : typeof data.email === 'string' ? data.email : uid,
              role: typeof data.role === 'string' ? data.role : 'MEMBER',
            }
          })
          .filter((entry): entry is MemberOption => entry !== null)
          .sort((a, b) => a.name.localeCompare(b.name))
        setMembers(next)
      })
      .catch(() => setMembers([]))
  }, [canManage, workspaceId])

  const copyText = useCallback(async (value: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) await navigator.clipboard.writeText(value)
  }, [])

  const toggleTarget = useCallback((id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else if (next.size < 8) next.add(id)
      return next
    })
  }, [])

  const create = useCallback(async () => {
    if (selectedIds.size === 0) {
      setError('Select at least one task/checklist.')
      setStatus(null)
      return
    }
    setBusy('create')
    setError(null)
    setStatus(null)
    try {
      const result = await createFieldPass({
        workspaceId,
        targetIds: Array.from(selectedIds),
        expiresInHours: 24,
        deleteOnCompletion,
      })
      const url = buildFieldPassUrl(result.token)
      setLastLink(url)
      await copyText(url).catch(() => undefined)
      setStatus('Field Pass link copied.')
      setSelectedIds(new Set())
      setPasses((current) => [result.pass, ...current.filter((entry) => entry.id !== result.pass.id)])
      setSelectedPassId(result.pass.id)
      setIsCreating(false)
      setRecordsUnavailable(false)
      await refreshPasses()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create Field Pass.')
    } finally {
      setBusy(null)
    }
  }, [copyText, deleteOnCompletion, refreshPasses, selectedIds, workspaceId])

  const revoke = useCallback(async (pass: FieldPassRecord) => {
    setBusy(`revoke:${pass.id}`)
    setError(null)
    setStatus(null)
    try {
      await revokeFieldPass(pass.id)
      setStatus('Field Pass revoked.')
      setPasses((current) => current.map((entry) => (entry.id === pass.id ? { ...entry, status: 'REVOKED' } : entry)))
      await refreshPasses()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not revoke Field Pass.')
    } finally {
      setBusy(null)
    }
  }, [refreshPasses])

  const addMemberToChat = useCallback(async (pass: FieldPassRecord) => {
    const memberUid = memberSelection[pass.id]
    if (!memberUid) {
      setError('Choose a member to add to this Field Pass chat.')
      setStatus(null)
      return
    }
    setBusy(`member:${pass.id}`)
    setError(null)
    setStatus(null)
    try {
      await addFieldPassChatMember(pass.id, memberUid)
      setStatus('Member added to Field Pass chat.')
      await refreshPasses()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not add member to chat.')
    } finally {
      setBusy(null)
    }
  }, [memberSelection, refreshPasses])

  const registerCreatedPass = useCallback(async (
    pass: FieldPassRecord,
    url: string,
    options: { stayInCreate?: boolean } = {},
  ) => {
    setLastLink(url)
    await copyText(url).catch(() => undefined)
    setStatus('Field Pass link copied.')
    setError(null)
    setSelectedIds(new Set())
    setPasses((current) => [pass, ...current.filter((entry) => entry.id !== pass.id)])
    setSelectedPassId(pass.id)
    if (!options.stayInCreate) setIsCreating(false)
    setRecordsUnavailable(false)
    await refreshPasses()
  }, [copyText, refreshPasses])

  const startCreate = useCallback(() => {
    setIsCreating(true)
    setSelectedPassId(null)
  }, [])

  const selectPass = useCallback((id: string) => {
    setSelectedPassId(id)
    setIsCreating(false)
  }, [])

  return {
    // data
    passes,
    workspaceId,
    activePasses,
    archivedPasses,
    visiblePasses,
    options,
    members,
    memberSelection,
    setMemberSelection,
    selectedIds,
    selectedPass,
    isCreating,
    view,
    setView,
    lastLink,
    busy,
    error,
    status,
    recordsUnavailable,
    deleteOnCompletion,
    setDeleteOnCompletion,
    // actions
    toggleTarget,
    create,
    revoke,
    addMemberToChat,
    registerCreatedPass,
    startCreate,
    selectPass,
    copyText,
    setStatus,
    setError,
  }
}

export type FieldPassesModel = ReturnType<typeof useFieldPassesModel>
